import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'

type FileInputProps = {
  /** Currently staged file (not yet uploaded). */
  value: File | null
  onChange: (file: File | null) => void
  /** Existing remote image to preview when no new file is staged. */
  existingUrl?: string | null
  label?: string
  accept?: string
  disabled?: boolean
  id?: string
  /**
   * HER-323: max allowed file size in bytes. A selection larger than this is
   * rejected up front (inline error + not staged) so it never reaches the API
   * — otherwise the skill row was created and only the icon upload failed,
   * leaving an iconless skill. Should mirror the server `MAX_FILE_SIZE`.
   */
  maxSize?: number
  /** Message shown when a selected file exceeds `maxSize`. */
  maxSizeMessage?: string
}

/**
 * Presentational, non-RHF image picker with a live preview. Used by the skill
 * form dialog: the chosen File is uploaded through the dedicated multipart
 * endpoint after the skill row itself is created/updated, so it is tracked as
 * plain component state rather than a form field.
 */
export function FileInput({
  value,
  onChange,
  existingUrl,
  label,
  accept = 'image/png,image/jpeg,image/webp',
  disabled,
  id = 'file-input',
  maxSize,
  maxSizeMessage,
}: FileInputProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // HER-323: reject an oversized file at selection so it is never staged or
  // uploaded — keeps create-then-upload from leaving an iconless skill.
  const handleSelect = (file: File | null) => {
    if (file && maxSize != null && file.size > maxSize) {
      setError(maxSizeMessage ?? 'File is too large.')
      if (inputRef.current) inputRef.current.value = ''
      onChange(null)
      return
    }
    setError(null)
    onChange(file)
  }

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(value)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const shownPreview = previewUrl ?? existingUrl ?? null

  const handleClear = () => {
    onChange(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="flex items-center gap-3">
        {shownPreview ? (
          <img src={shownPreview} alt="" className="h-12 w-12 rounded-md border object-cover" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground">
            {t('skills.noIcon')}
          </div>
        )}
        <Input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={(e) => handleSelect(e.target.files?.[0] ?? null)}
          className="max-w-xs"
        />
        {value && (
          <Button type="button" variant="outline" size="sm" onClick={handleClear} disabled={disabled}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
